import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Paper,
  Stack,
  Typography,
  useTheme,
  IconButton,
  GridLegacy as Grid,
} from "@mui/material";
import { ContactRoleChipLegend, ContactRoleChipList } from "components/Contacts/ContactRoleChip";
import CopyableTypography from "components/Typography/CopyableTypography";
import { INTERNAL_ONLY_CONTACT_ROLES } from "encamp-shared/src/tierii/contacts";
import { formatAddress } from "encamp-shared/src/utils/address";
import { getFullName } from "encamp-shared/src/utils/name";
import { TierIiReportOrgStatus } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useMemo } from "react";

import { ContactReportRole, Person } from "../../../../generated-graphql/graphql";
import { ShowMoreCollapse } from "./ShowMoreCollapse";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

export function ContactDetails() {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-contacts-expanded",
    { defaultValue: true }
  );

  const showContacts = useMemo(() => {
    return !!data?.tierIIReport.verifierFullName || !!data?.tierIIReport.facility.contacts?.length;
  }, [data?.tierIIReport.facility.contacts?.length, data?.tierIIReport.verifierFullName]);

  const verifier = useMemo(() => {
    if (data?.tierIIReport.organizationStatus === TierIiReportOrgStatus.Verified) {
      return {
        title: "Verifier",
        person: undefined,
        verifierFullName: data?.tierIIReport.verifierFullName,
        verifierTitle: data?.tierIIReport.verifierTitle,
        verifierEmail: data?.tierIIReport.verifierEmail,
        verifierPhone: data?.tierIIReport.verifierPhone,
      };
    }
    return null;
  }, [data]);

  const facilityContacts = useMemo(() => {
    return (
      data?.tierIIReport.facility.contacts
        ?.map((contact: any) => {
          const reportableRoles = (contact.reportingRoles ?? []).filter(
            (role: ContactReportRole) => !INTERNAL_ONLY_CONTACT_ROLES.includes(role)
          );
          return {
            roles: reportableRoles as ContactReportRole[],
            person: contact.person,
            hasReportableRoles: reportableRoles.length > 0,
          };
        })
        .filter((contact) => contact.hasReportableRoles) || []
    );
  }, [data]);

  if (!showContacts) {
    return null;
  }

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      sx={{
        padding: theme.spacing(3),
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: 3,
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Contacts</Typography>
        <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      {/* Collapsed view: Show 2 columns - Verifier on left, contacts on right */}
      {!isExpanded && (
        <Grid container spacing={3}>
          {/* Left column: Verifier */}
          <Grid item xs={12} md={6}>
            {verifier && (
              <Stack direction="column" spacing={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", color: "text.secondary" }}
                >
                  {verifier.title}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: "80px",
                      color: theme.palette.text.secondary,
                      fontSize: "12px",
                      lineHeight: "12px",
                    }}
                  >
                    Name
                  </Typography>
                  <CopyableTypography
                    text={verifier.verifierFullName || getFullName(verifier.person) || "--"}
                    sx={{ fontSize: "16px", lineHeight: "1.5" }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: "80px",
                      color: theme.palette.text.secondary,
                      fontSize: "12px",
                      lineHeight: "12px",
                    }}
                  >
                    Title
                  </Typography>
                  <CopyableTypography
                    text={verifier.verifierTitle || "--"}
                    sx={{ fontSize: "16px", lineHeight: "1.5" }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: "80px",
                      color: theme.palette.text.secondary,
                      fontSize: "12px",
                      lineHeight: "12px",
                    }}
                  >
                    Email
                  </Typography>
                  <CopyableTypography
                    text={verifier.verifierEmail || "--"}
                    sx={{ fontSize: "16px", lineHeight: "1.5" }}
                  />
                </Stack>
              </Stack>
            )}
          </Grid>

          {/* Right column: Facility contacts */}
          <Grid item xs={12} md={6}>
            <Stack direction="column" spacing={1}>
              {facilityContacts.map((contact: any, index: number) => (
                <Stack key={index} direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: "50%" }}>
                    <ContactRoleChipList roles={contact.roles} />
                  </Box>
                  <CopyableTypography
                    text={getFullName(contact.person) || "--"}
                    sx={{
                      width: "50%",
                      fontSize: "16px",
                      lineHeight: "1.5",
                      wordBreak: "break-word",
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      )}

      <ShowMoreCollapse isExpanded={isExpanded} onExpand={() => setIsExpanded(true)}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: theme.spacing(3),
          }}
        >
          {verifier && (
            <ContactCard
              title={verifier.title}
              person={verifier.person}
              verifierFullName={verifier.verifierFullName}
              verifierTitle={verifier.verifierTitle}
              verifierEmail={verifier.verifierEmail}
              verifierPhone={verifier.verifierPhone}
            />
          )}
          {facilityContacts.map((contact: any, index: number) => (
            <ContactCard key={index} roles={contact.roles} person={contact.person} />
          ))}
        </Box>
        <ContactRoleChipLegend />
      </ShowMoreCollapse>
    </Stack>
  );
}

interface ContactCardProps {
  title?: string;
  roles?: ContactReportRole[];
  person?: Person | null;
  verifierFullName?: string | null;
  verifierTitle?: string | null;
  verifierPhone?: string | null;
  verifierEmail?: string | null;
}

function ContactCard({
  title,
  roles,
  person,
  verifierFullName,
  verifierTitle,
  verifierEmail,
  verifierPhone,
}: ContactCardProps) {
  const theme = useTheme();

  const displayName = useMemo(() => {
    if (verifierFullName) {
      return verifierFullName;
    }
    return getFullName(person) || "--";
  }, [person, verifierFullName]);

  const displayTitle = useMemo(() => {
    if (verifierTitle) {
      return verifierTitle;
    }
    return person?.title || "--";
  }, [person, verifierTitle]);

  const displayEmail = useMemo(() => {
    if (verifierEmail) {
      return verifierEmail;
    }
    return person?.email || "--";
  }, [person, verifierEmail]);

  const displayPhones = useMemo(() => {
    if (verifierPhone) {
      return verifierPhone;
    }
    if (!person?.phones || person.phones.length === 0) {
      return "--";
    }

    return person.phones.map((phone: any) => (
      <Typography key={phone.type + phone.number} variant="body2" component="div">
        {phone.type}: {phone.number}
      </Typography>
    ));
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [person]);

  const displayAddress = useMemo(() => {
    if (!person) {
      return "--";
    }
    return formatAddress(person.streetAddress1, person.city, person.state, person.zip) || "--";
  }, [person]);

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {roles && roles.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <ContactRoleChipList roles={roles} />
        </Box>
      ) : (
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "text.secondary", mb: 2 }}>
          {title}
        </Typography>
      )}

      <Stack direction="column" spacing={1}>
        <ContactField label="Name" value={displayName} />
        <ContactField label="Title" value={displayTitle} />
        <ContactField label="Email" value={displayEmail} />
        <ContactField label="Phone(s)" value={displayPhones} />
        <ContactField label="Address" value={displayAddress} />
      </Stack>
    </Stack>
  );
}

interface ContactFieldProps {
  label: string;
  value: string | React.ReactNode;
}

function ContactField({ label, value }: ContactFieldProps) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          minWidth: "80px",
          color: theme.palette.text.secondary,
          fontSize: "12px",
          lineHeight: "12px",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {typeof value === "string" ? (
          <CopyableTypography text={value} sx={{ fontSize: "16px", lineHeight: "1.5" }} />
        ) : (
          value
        )}
      </Box>
    </Stack>
  );
}
